import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusPill, STATUS_PILL_ROW_COMPACT_CLASS } from "../../components/StatusPill";
import { toast } from "sonner";
import { Check, ChevronDown, Pencil } from "lucide-react";
import ClientBoardPage from "./ClientBoardPage";
import ApprovalsPage from "./ApprovalsPage";
import FilesPage from "./FilesPage";
import { OFFERING_PHASES, canonicalOffering } from "../../config/offeringPhases";
import { cn } from "../../lib/utils";
import {
  buildCycleLabelMap,
  engagementTimelineProgressPercent,
  formatCycleRangeLabel,
} from "../../lib/cycleLabels";
import { motion, useReducedMotion } from "framer-motion";
import { TimePickerField } from "../../components/TimePickerField";
import { Calendar } from "../../components/ui/calendar";
import { InlineDateRangePicker } from "../../components/InlineDateRangePicker";
import { FullScreenModal } from "../../components/FullScreenModal";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { AppAvatar } from "../../components/AppAvatar";
import { usePortalParticipants } from "../../context/PortalParticipantsContext";
import { assigneeAvatarSrc as resolveAssigneeAvatarSrc } from "../../lib/portalAvatars";
const TRACKER_TABS = [
  { id: "phases", num: "01", label: "PHASE" },
  { id: "approvals", num: "02", label: "APPROVALS", clientOnly: true },
  { id: "library", num: "03", label: "LIBRARY", clientOnly: true },
  { id: "notes", num: "04", label: "NOTES", clientOnly: true },
];

function formatProjectTimelineLabel(client, activeCycle) {
  if (client?.engagement_type === "monthly" && activeCycle) {
    return formatCycleRangeLabel(activeCycle) || "—";
  }
  const start = client?.timeline_start ? new Date(client.timeline_start) : null;
  const end = client?.timeline_end ? new Date(client.timeline_end) : null;
  if (!start || Number.isNaN(start.getTime())) {
    if (!end || Number.isNaN(end.getTime())) return "—";
    return end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  if (!end || Number.isNaN(end.getTime())) {
    return start.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const left = start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const right = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${left} – ${right}`;
}

function timelineSpanLabel(client, activeCycle) {
  let start = null;
  let end = null;
  if (client?.engagement_type === "monthly" && activeCycle) {
    start = activeCycle.start_date ? new Date(activeCycle.start_date) : null;
    const endRaw = activeCycle.end_date || activeCycle.range_end;
    end = endRaw ? new Date(endRaw) : null;
  } else {
    start = client?.timeline_start ? new Date(client.timeline_start) : null;
    end = client?.timeline_end ? new Date(client.timeline_end) : null;
  }
  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) return null;
  const weeks = Math.max(1, Math.round((end.getTime() - start.getTime()) / (7 * 86400000)));
  return `~${weeks} week${weeks === 1 ? "" : "s"}`;
}

const MILESTONE_STATUS_MENU = [
  { value: "not_started", label: "NOT STARTED" },
  { value: "in_progress", label: "IN MOTION" },
  { value: "complete", label: "COMPLETED" },
];

const MILESTONE_RESPONSIBLE_AVATAR_SIZE = 22;

function milestoneStatusPillTone(status) {
  if (status === "complete") return "ok";
  if (status === "in_progress") return "accent";
  return "meta";
}

const FOUNDATION_SUBTITLE = "Initial meeting, present state audit & framework delivery.";

const MD_UP_MEDIA = "(min-width: 768px)";

function useIsMdUp() {
  const [isMdUp, setIsMdUp] = useState(
    () => (typeof window !== "undefined" ? window.matchMedia(MD_UP_MEDIA).matches : false)
  );
  useEffect(() => {
    const mq = window.matchMedia(MD_UP_MEDIA);
    const onChange = () => setIsMdUp(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMdUp;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseLocalDateOnly(value) {
  if (!value) return null;
  const raw = String(value).trim();
  // Must be exactly YYYY-MM-DD — ISO datetimes (with T/time) are not date-only.
  if (!DATE_ONLY_RE.test(raw)) return null;
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/** Display milestone instant in local wall time (date + time when meaningful). */
function formatMilestoneDateTime(iso) {
  if (!iso) return "";
  const d = parseLocalDateOnly(iso) || new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Date-only values and legacy midnight / end-of-day sentinels show as dates without time. */
function formatMilestoneDueDisplay(iso) {
  if (!iso) return "";
  const localDate = parseLocalDateOnly(iso);
  if (localDate) {
    return localDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (
    (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)
    || (d.getHours() === 23 && d.getMinutes() === 59 && d.getSeconds() === 0)
  ) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  return formatMilestoneDateTime(iso);
}

function formatMilestoneRangeDisplay(startIso, endIso) {
  if (!startIso && !endIso) return "";
  if (startIso && endIso) {
    const start = formatMilestoneDueDisplay(startIso);
    const end = formatMilestoneDueDisplay(endIso);
    return start === end ? start : `${start} – ${end}`;
  }
  return formatMilestoneDueDisplay(startIso || endIso);
}

function milestoneDueLabel(m, noDuePrefix) {
  if (!m?.due_date) return "";
  const formatted = formatMilestoneDueDisplay(m.due_date);
  return noDuePrefix ? formatted : `Due ${formatted}`;
}

/** Build stored value from local YYYY-MM-DD; empty time stores date-only. */
function localDateTimeToIso(dateStr, timeStr) {
  if (!dateStr) return null;
  const dateOnly = dateStr.slice(0, 10);
  if (!DATE_ONLY_RE.test(dateOnly)) return null;
  const empty = timeStr == null || String(timeStr).trim() === "";
  if (empty) return dateOnly;
  const [y, mo, d] = dateOnly.split("-").map((n) => Number(n));
  const [hh, mm] = String(timeStr).slice(0, 5).split(":").map((n) => Number(n));
  return new Date(y, mo - 1, d, Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0).toISOString();
}

function milestoneTimelineFromRecord(milestone) {
  if (!milestone) return { startIso: "", endIso: "" };
  const hasRange = Boolean(milestone.start_date || milestone.end_date);
  if (hasRange) {
    return {
      startIso: milestone.start_date || "",
      endIso: milestone.end_date || "",
    };
  }
  if (milestone.due_date) {
    return { startIso: milestone.due_date, endIso: "" };
  }
  return { startIso: "", endIso: "" };
}

function timelineRangeToIso(range) {
  const startIso = range?.from ? localDateTimeToIso(range.from, range.fromTime) : null;
  const endIso = range?.to ? localDateTimeToIso(range.to, range.toTime) : null;
  return {
    startIso: startIso || "",
    endIso: endIso || "",
  };
}

function resolveTrackerTab(searchParams, visibleTabs) {
  const tab = searchParams.get("tab");
  if (tab && visibleTabs.some((item) => item.id === tab)) return tab;
  if (searchParams.get("milestone")) return "phases";
  if (searchParams.get("note")) return "notes";
  if (searchParams.get("approval")) return "approvals";
  return visibleTabs[0]?.id || "phases";
}

export default function ProjectTracker() {
  const { client, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cycleDesktopSlotEl, setCycleDesktopSlotEl] = useState(null);
  const isAdmin = user?.role === "admin";
  const visibleTabs = useMemo(
    () =>
      TRACKER_TABS.filter((tab) => {
        if (tab.adminOnly && !isAdmin) return false;
        if (tab.clientOnly && isAdmin) return false;
        return true;
      }),
    [isAdmin],
  );
  const view = useMemo(
    () => resolveTrackerTab(searchParams, visibleTabs),
    [searchParams, visibleTabs],
  );
  const phases = OFFERING_PHASES[canonicalOffering(client?.service_tier)] || OFFERING_PHASES.web_design;

  /** Client-only header slot; admin switches timelines from the Plan date itself. */
  const showPlanningCycleSlot =
    !isAdmin &&
    view === "phases" &&
    client?.engagement_type === "monthly" &&
    canonicalOffering(client?.service_tier) === "operational_ecosystem";

  const setCycleDesktopSlotRef = useCallback((node) => {
    setCycleDesktopSlotEl(node);
  }, []);

  const setView = useCallback((tabId) => {
    if (!visibleTabs.some((tab) => tab.id === tabId)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tabId);
      return next;
    }, { replace: true });
  }, [setSearchParams, visibleTabs]);

  useEffect(() => {
    if (searchParams.get("tab")) return;
    const inferred = resolveTrackerTab(searchParams, visibleTabs);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", inferred);
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, visibleTabs]);

  if (!client) return <div className="p-6 sm:p-12 t-meta">No client context.</div>;

  const showTabBar = visibleTabs.length > 1;

  return (
    <div
      className="p-4 sm:p-5 md:p-7 t-fade-up min-w-0 max-w-full overflow-x-hidden"
      data-testid="tracker-page"
    >
      {isAdmin ? (
        <header className="mb-6 sm:mb-8">
          <div className="label-eyebrow mb-2">Plan</div>
          <h1 className="font-heading text-2xl tracking-tight !font-extralight md:text-3xl">
            {client.name}
          </h1>
        </header>
      ) : null}

      {showTabBar || showPlanningCycleSlot ? (
        <div
          className={`mb-8 sm:mb-10 w-full max-w-full min-w-0 ${showPlanningCycleSlot ? "md:flex md:items-end md:justify-between md:gap-6" : ""}`}
        >
          {showTabBar ? (
            <div
              className={`max-w-full overflow-x-auto border-b border-[var(--t-border)] [-webkit-overflow-scrolling:touch] ${showPlanningCycleSlot ? "md:min-w-0 md:flex-1" : "w-full"}`}
              data-testid="tracker-tabs"
            >
              <div className="flex w-max flex-nowrap gap-0 pb-px px-1 sm:px-2 md:px-0">
                {visibleTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setView(t.id)}
                    className={`shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm border-b-2 -mb-px min-h-[44px] flex items-center gap-2 whitespace-nowrap transition-colors ${view === t.id ? "border-[var(--t-accent)] text-[var(--t-text)]" : "border-transparent text-[var(--t-meta)] hover:text-[var(--t-text)]"}`}
                    data-testid={`tab-${t.id}`}
                  >
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : showPlanningCycleSlot ? (
            <div className="md:min-w-0 md:flex-1" />
          ) : null}
          {showPlanningCycleSlot ? (
            <div
              ref={setCycleDesktopSlotRef}
              className="hidden shrink-0 md:flex md:items-center md:justify-end md:pb-px"
              data-testid="planning-cycle-desktop-slot"
            />
          ) : null}
        </div>
      ) : null}

      <div>
        {view === "phases" && (
          <PhasesView client={client} user={user} isAdmin={isAdmin} phases={phases} cycleDesktopSlotEl={cycleDesktopSlotEl} />
        )}
        {view === "approvals" && <ApprovalsPage embedded />}
        {view === "library" && <FilesPage embedded />}
        {view === "notes" && <ClientBoardPage embedded />}
      </div>
    </div>
  );
}

const PhasesView = ({ client, user, isAdmin, phases, cycleDesktopSlotEl }) => {
  const isMdUp = useIsMdUp();
  const reduce = useReducedMotion();
  const participants = usePortalParticipants();
  const [searchParams] = useSearchParams();
  const [milestones, setMilestones] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [activeCycleId, setActiveCycleId] = useState(null);
  const [cycleOpen, setCycleOpen] = useState(false);
  const [loadingCycleAction, setLoadingCycleAction] = useState(false);
  const [milestoneModalId, setMilestoneModalId] = useState(null);
  const [createMilestoneOpen, setCreateMilestoneOpen] = useState(false);
  const milestoneContainerRefs = useRef({});
  const didInitPhaseRef = useRef(false);
  const [activePhase, setActivePhase] = useState("foundation");
  const [milestoneStatusMenuOpenId, setMilestoneStatusMenuOpenId] = useState(null);
  const [milestoneResponsibleMenuOpenId, setMilestoneResponsibleMenuOpenId] = useState(null);
  const phaseMemoryRef = useRef({}); // { [serviceTier]: phaseKey }
  const prevServiceTierRef = useRef(client?.service_tier);
  const activeCycleIdRef = useRef(activeCycleId);
  const params = user?.role === "admin" ? `?client_id=${client?.id}` : "";
  const isMonthly = client?.engagement_type === "monthly";
  const phaseByKey = useMemo(() => {
    const map = {};
    (phases || []).forEach((p) => { map[p.key] = p; });
    return map;
  }, [phases]);

  /** Name shown for milestone responsibility `client` (portal primary contact). */
  const portalContactDisplayName = useMemo(() => {
    const fromClient = (client?.primary_contact_name || "").trim();
    if (fromClient) return fromClient;
    if (user?.role === "client") return (user?.name || "").trim();
    return "";
  }, [client?.primary_contact_name, user?.role, user?.name]);

  const responsibleMenuOptions = useMemo(
    () => [{ value: "taistu" }, { value: "shared" }, { value: "client" }],
    [],
  );

  const studioAvatarPerson = useMemo(
    () => ({
      key: "studio",
      name: participants?.studio?.name || "Taī",
      src: resolveAssigneeAvatarSrc("Taī", { participants, user, client }),
    }),
    [client, participants, user],
  );

  const clientAvatarPerson = useMemo(
    () => ({
      key: "client",
      name: portalContactDisplayName || participants?.client?.name || "Client",
      src: resolveAssigneeAvatarSrc(
        portalContactDisplayName || participants?.client?.name || "Client",
        { participants, user, client },
      ),
    }),
    [client, participants, portalContactDisplayName, user],
  );

  const responsibleAriaLabel = useCallback(
    (raw) => {
      const key = String(raw || "taistu").toLowerCase();
      if (key === "client") return `Assigned to ${clientAvatarPerson.name}`;
      if (key === "shared") return "Assigned to studio and client";
      return "Assigned to studio";
    },
    [clientAvatarPerson.name],
  );

  const renderResponsibleMark = useCallback(
    (raw, size = MILESTONE_RESPONSIBLE_AVATAR_SIZE) => {
      const key = String(raw || "taistu").toLowerCase();
      if (key === "shared") {
        return (
          <span className="inline-flex items-center gap-1.5">
            <AppAvatar name={studioAvatarPerson.name} src={studioAvatarPerson.src} size={size} className="shrink-0" />
            <AppAvatar name={clientAvatarPerson.name} src={clientAvatarPerson.src} size={size} className="shrink-0" />
          </span>
        );
      }
      const person = key === "client" ? clientAvatarPerson : studioAvatarPerson;
      return <AppAvatar name={person.name} src={person.src} size={size} className="shrink-0" />;
    },
    [clientAvatarPerson, studioAvatarPerson],
  );

  useEffect(() => {
    activeCycleIdRef.current = activeCycleId;
  }, [activeCycleId]);

  const isOperationalEcosystem = canonicalOffering(client?.service_tier) === "operational_ecosystem";
  const activateReady = useMemo(() => {
    if (!isOperationalEcosystem) return false;
    // Activate highlights ONLY when all non-ongoing milestones are complete.
    const relevant = milestones.filter((m) => m.phase !== "ongoing" && m.phase !== "launch");
    if (relevant.length === 0) return false;
    return relevant.every((m) => m.status === "complete");
  }, [isOperationalEcosystem, milestones]);

  // Existing clients may have the older seeded descriptions in DB. Prefer the config subtitle
  // for the phase "header milestone" (the seeded milestone whose title matches the phase label).
  const SEEDED_DESCRIPTIONS = useMemo(() => new Set([
    "Kickoff & immersion",
    "Brand & strategy direction",
    "Production & build sprint",
    "Launch and post-launch tuning",
    "Define scope and operational strategy",
    "Implementation & systems build",
    "Delivery, training, iteration",
  ]), []);

  const load = useCallback(async (cycleOverride) => {
    let query = params;
    const selectedCycle = typeof cycleOverride === "string" ? cycleOverride : activeCycleIdRef.current;
    if (isMonthly && selectedCycle) {
      query = params ? `${params}&cycle_id=${selectedCycle}` : `?cycle_id=${selectedCycle}`;
    }
    const { data } = await api.get(`/portal/milestones${query}`);
    setMilestones(data);
    // Only choose an initial phase once; after that, user controls phase navigation.
    if (!didInitPhaseRef.current) {
      const remembered = phaseMemoryRef.current?.[client?.service_tier];
      if (remembered && (phases || []).some((p) => p.key === remembered)) {
        setActivePhase(remembered);
        didInitPhaseRef.current = true;
        return;
      }
      const inProg = data.find((m) => m.status === "in_progress");
      if (inProg) {
        setActivePhase(inProg.phase);
      } else {
        const order = (phases || []).map((p) => p.key);
        const firstIncomplete = data.find((m) => m.status !== "complete" && order.includes(m.phase));
        if (firstIncomplete) setActivePhase(firstIncomplete.phase);
        else setActivePhase("ongoing");
      }
      didInitPhaseRef.current = true;
    }
  }, [isMonthly, params, phases]);

  const loadCycles = useCallback(async () => {
    if (!isMonthly) {
      setCycles([]);
      setActiveCycleId(null);
      return;
    }
    const { data } = await api.get(`/portal/cycles${params}`);
    setCycles(data.cycles || []);
    const preferred = data.active_cycle_id || data.cycles?.[0]?.id || null;
    setActiveCycleId(preferred);
    await load(preferred);
  }, [isMonthly, load, params]);

  useEffect(() => {
    // Remember the user's phase selection per type so switching types and switching back
    // returns you to the phase you were on.
    const prev = prevServiceTierRef.current;
    const next = client?.service_tier;
    if (prev && next && prev !== next) {
      phaseMemoryRef.current[prev] = activePhase;
      prevServiceTierRef.current = next;
    } else if (next && !prev) {
      prevServiceTierRef.current = next;
    }
  }, [activePhase, client?.service_tier]);

  useEffect(() => {
    didInitPhaseRef.current = false;
    if (isMonthly) {
      loadCycles();
    } else {
      load(null);
    }
  }, [client, isMonthly, load, loadCycles]);

  useEffect(() => {
    const milestoneId = searchParams.get("milestone");
    if (!milestoneId || milestones.length === 0) return;
    const item = milestones.find((m) => m.id === milestoneId);
    if (!item) return;
    setActivePhase(item.phase);
    requestAnimationFrame(() => {
      milestoneContainerRefs.current[milestoneId]?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }, [milestones, searchParams]);

  useEffect(() => {
    if (!isMonthly || !activeCycleId) return;
    load(activeCycleId);
  }, [activeCycleId, isMonthly, load]);

  const updateStatus = async (m, status) => {
    if (!isAdmin) return;
    await api.patch(`/portal/milestones/${m.id}`, { status });
    toast.success("Updated");
    load();
  };

  const updateResponsible = async (m, responsible) => {
    if (!isAdmin) return;
    await api.patch(`/portal/milestones/${m.id}`, { responsible });
    toast.success("Updated");
    load();
  };

  const toggleDeliverable = async (m, deliverableId) => {
    if (!isAdmin) return;
    const dels = m.deliverables.map((d) => d.id === deliverableId ? { ...d, done: !d.done } : d);
    await api.patch(`/portal/milestones/${m.id}`, { deliverables: dels });
    load();
  };

  const openMilestoneModal = (m) => {
    if (!isAdmin) return;
    setMilestoneModalId(m.id);
  };

  const createNextCycle = async () => {
    if (!isAdmin || !isMonthly) return;
    setLoadingCycleAction(true);
    try {
      const active = cycles.find((c) => c.id === activeCycleId) || cycles[0];
      const base = (() => {
        const raw = active?.start_date || null;
        const d = raw ? new Date(raw) : new Date();
        return Number.isNaN(d.getTime()) ? new Date() : d;
      })();
      const nextMonth = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 1, 0, 0, 0));
      await api.post(`/admin/clients/${client.id}/cycles`, {
        with_rollover: true,
        rollover_from_cycle_id: active?.id || null,
        start_date: nextMonth.toISOString(),
      });
      toast.success("New cycle created");
      await loadCycles();
    } catch {
      toast.error("Could not create cycle");
    } finally {
      setLoadingCycleAction(false);
    }
  };

  const activeCycle = useMemo(
    () => cycles.find((c) => c.id === activeCycleId) || cycles[0] || null,
    [activeCycleId, cycles],
  );

  const canCreateNextCycle = useMemo(() => {
    if (!isAdmin || !isMonthly) return false;
    // Only allow creation starting on the 20th in America/New_York (EST/EDT).
    const now = new Date();
    const nyDay = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", day: "2-digit" }).format(now));
    const nyMonth = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "2-digit" }).format(now));
    const nyYear = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric" }).format(now));
    if (!nyDay || nyDay < 20) return false;
    // Only when viewing the current month cycle (so "next month" is deterministic).
    const c = activeCycle;
    if (!c?.start_date) return false;
    const d = new Date(c.start_date);
    if (Number.isNaN(d.getTime())) return false;
    return d.getUTCFullYear() === nyYear && (d.getUTCMonth() + 1) === nyMonth;
  }, [activeCycle, isAdmin, isMonthly]);

  const cycleLabelMap = useMemo(() => buildCycleLabelMap(cycles), [cycles]);

  const formatCycleLabel = useCallback((c) => {
    if (!c) return "";
    return c.range_label || cycleLabelMap.get(c.id) || "Cycle";
  }, [cycleLabelMap]);

  const timelineLabel =
    isMonthly && isOperationalEcosystem && activeCycle
      ? formatCycleLabel(activeCycle)
      : formatProjectTimelineLabel(client, activeCycle);
  const spanLabel = timelineSpanLabel(client, activeCycle);
  const timelineProgress = useMemo(() => {
    const pct = engagementTimelineProgressPercent(client, activeCycle, cycles);
    return { pct: pct ?? 0, hasWindow: pct != null };
  }, [activeCycle, client, cycles]);
  const progressPct = timelineProgress.pct;
  const hasTimelineWindow = timelineProgress.hasWindow;
  const activePhaseMeta = phaseByKey[activePhase];
  const canBrowseTimelines = isMonthly && isOperationalEcosystem;
  const timelineInteractive = isAdmin && canBrowseTimelines && (cycles.length > 0 || canCreateNextCycle);

  const cyclePickerMenu = (
    <PopoverContent
      align="start"
      side="bottom"
      sideOffset={8}
      collisionPadding={24}
      className="z-[60] w-[min(420px,90vw)] overflow-hidden rounded-2xl border border-white/50 p-0 text-[var(--t-text)] t-glass shadow-none"
    >
      <div className="rounded-t-[var(--t-event-pill-radius)] p-3 border-b border-[var(--t-border)] flex items-center justify-between gap-3">
        <div className="label-eyebrow">Timelines</div>
      </div>
      <div className="rounded-b-[var(--t-event-pill-radius)] p-2 max-h-[340px] overflow-auto">
        {cycles.map((c) => {
          const selected = c.id === activeCycleId;
          return (
            <button
              key={c.id}
              type="button"
              className={`w-full text-left px-3 py-2 min-h-[44px] border border-transparent hover:border-[var(--t-border)] ${selected ? "bg-[rgba(0,30,255,0.08)]" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveCycleId(c.id);
                load(c.id);
                setCycleOpen(false);
              }}
              data-testid={`cycle-option-${c.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className={`font-heading text-sm tracking-tight truncate ${c.status === "active" ? "text-[var(--t-accent)]" : ""}`}>
                  {formatCycleLabel(c)}
                </div>
                {selected && <div className="t-meta text-xs">Viewing</div>}
              </div>
            </button>
          );
        })}
        {cycles.length === 0 && <div className="t-meta text-xs px-3 py-4">No cycles yet.</div>}
        {isAdmin && (
          <>
            <div className="my-2 border-t border-[var(--t-border)]" />
            <button
              type="button"
              className="w-full text-left px-3 py-2 min-h-[44px] border border-transparent font-heading text-sm tracking-tight hover:border-[var(--t-border)] disabled:opacity-50 disabled:pointer-events-none"
              onMouseDown={(e) => {
                e.preventDefault();
                if (loadingCycleAction || !canCreateNextCycle) return;
                createNextCycle();
                setCycleOpen(false);
              }}
              disabled={loadingCycleAction || !canCreateNextCycle}
              data-testid="create-cycle-btn"
              title={!canCreateNextCycle ? "Available on the 20th or later (America/New_York) for the current month cycle." : undefined}
            >
              {loadingCycleAction ? "Creating…" : "New cycle"}
            </button>
          </>
        )}
      </div>
    </PopoverContent>
  );

  return (
    <>
      {isAdmin ? (
        <div className="neo-panel mb-6 p-4 sm:mb-8 sm:p-5" data-testid="plan-timeline">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="label-eyebrow mb-2">Project timeline</div>
              {timelineInteractive ? (
                <Popover open={cycleOpen} onOpenChange={setCycleOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`font-heading text-left text-xl tracking-tight !font-light md:text-2xl hover:text-[var(--t-accent)] ${
                        activeCycle?.status === "active" ? "text-[var(--t-accent)]" : ""
                      }`}
                      data-testid="plan-timeline-trigger"
                      aria-expanded={cycleOpen}
                      aria-haspopup="listbox"
                    >
                      {timelineLabel}
                    </button>
                  </PopoverTrigger>
                  {cyclePickerMenu}
                </Popover>
              ) : (
                <div className="font-heading text-xl tracking-tight !font-light md:text-2xl">
                  {timelineLabel}
                </div>
              )}
            </div>
            <div className="text-right text-xs text-[var(--t-meta)]">
              <div>
                {activePhaseMeta?.label || "Phase"}
                {hasTimelineWindow ? ` · ${progressPct}%` : ""}
              </div>
              {spanLabel ? <div className="mt-1">{spanLabel}</div> : null}
            </div>
          </div>
          {hasTimelineWindow ? (
            <div className="neo-well mt-4 h-2 overflow-hidden rounded-full">
              <motion.div
                className="h-full rounded-full bg-[var(--t-accent)]"
                initial={reduce ? false : { width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="plan-timeline-progress"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {!isAdmin && canBrowseTimelines && (
        <Popover open={cycleOpen} onOpenChange={setCycleOpen}>
          {isMdUp && cycleDesktopSlotEl != null &&
            createPortal(
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] inline-flex min-h-[32px] max-w-[min(100%,280px)] shrink-0 items-center gap-2 px-3 py-1.5 text-left"
                  data-testid="cycle-select"
                >
                  <span className="label-eyebrow shrink-0">Timeline</span>
                  <span className={`min-w-0 flex-1 truncate text-[11px] !font-light cursor-pointer ${activeCycle?.status === "active" ? "text-[var(--t-accent)]" : ""}`}>
                    {activeCycle ? formatCycleLabel(activeCycle) : "Select timeline"}
                  </span>
                </button>
              </PopoverTrigger>,
              cycleDesktopSlotEl
            )}
          {!isMdUp && (
            <div className="neo-panel p-4 sm:p-5 mb-6 sm:mb-8">
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] w-full min-h-[44px] justify-between px-4 inline-flex items-center gap-3"
                  data-testid="cycle-select-mobile"
                >
                  <span className="label-eyebrow">Timeline</span>
                  <span
                    className={`truncate max-w-[220px] sm:max-w-[320px] text-[11px] !font-light cursor-pointer ${
                      activeCycle?.status === "active" ? "text-[var(--t-accent)]" : ""
                    }`}
                  >
                    {activeCycle ? formatCycleLabel(activeCycle) : "Select timeline"}
                  </span>
                </button>
              </PopoverTrigger>
            </div>
          )}
          {cyclePickerMenu}
        </Popover>
      )}

      <div className="neo-panel overflow-hidden">
        <div
          className="grid grid-flow-col auto-cols-[minmax(12rem,1fr)] gap-px overflow-x-auto bg-[var(--t-border)] sm:overflow-visible sm:grid-flow-row sm:grid-cols-2 lg:grid-cols-4"
          data-testid="phase-ribbon"
        >
        {phases.map((p) => {
          const pms = milestones.filter((m) => m.phase === p.key);
          const done = pms.filter((m) => m.status === "complete").length;
          const total = pms.length;
          const isActivate = isOperationalEcosystem && p.key === "launch";
          const isActive = isActivate ? activateReady : (p.key === activePhase);
          const activateHighlight = isActivate && activateReady;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => { if (!isActivate) setActivePhase(p.key); }}
              className={`phase-tile p-4 sm:p-6 text-left border-l-2 min-h-[44px] sm:min-h-0 ${
                activateHighlight ? "border-l-[var(--t-accent)]" : ""
              } ${!activateHighlight && isActive ? "border-l-[var(--t-accent)]" : (!activateHighlight ? "border-l-transparent" : "")} ${isActivate ? "cursor-default" : ""}`}
              data-testid={`phase-${p.key}`}
              aria-disabled={isActivate ? "true" : "false"}
            >
              <div className="flex flex-col h-full">
                <div className="t-meta mb-2 leading-none h-[14px] flex items-start">{p.num}</div>
                <div className={`font-heading !font-light text-sm sm:text-base tracking-tight mb-2 sm:mb-3 min-h-[40px] ${isActive ? "text-[var(--t-text)]" : "text-[var(--t-meta)]"}`}>
                  {p.label}
                </div>
                {(p.subtitle || p.key === "foundation") && (
                  <div className="t-meta text-xs mb-3 min-h-[48px]">
                    {p.key === "foundation" ? FOUNDATION_SUBTITLE : p.subtitle}
                  </div>
                )}
                <div className="flex-1" />
                {!isActivate && total > 0 && (
                  <div className="t-meta text-xs mt-2 sm:mt-3 pb-1 sm:pb-0">
                    {done}/{total} milestones
                  </div>
                )}
              </div>
            </button>
          );
        })}
        </div>
      </div>

      <div className="space-y-3 mt-6 sm:mt-8 mb-6 sm:mb-8" data-testid="milestones-list">
        {milestones.filter((m) => m.phase === activePhase).map((m) => {
          const displayIntention =
            (phaseByKey[m.phase]?.subtitle && m.title === phaseByKey[m.phase]?.label && (SEEDED_DESCRIPTIONS.has(m.description) || !m.description))
              ? phaseByKey[m.phase].subtitle
              : (m.description || "").trim();
          return (
          <div
            key={m.id}
            ref={(el) => { milestoneContainerRefs.current[m.id] = el; }}
            className="group milestone-card p-4 sm:p-6"
            data-testid={`milestone-${m.id}`}
          >
            <div className="min-w-0 w-full">
              <div className="mb-2 flex min-w-0 items-start gap-2 sm:gap-3">
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {isAdmin ? (
                    <Popover open={milestoneStatusMenuOpenId === m.id} onOpenChange={(open) => setMilestoneStatusMenuOpenId(open ? m.id : null)}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "t-pill inline-flex shrink-0 cursor-pointer border bg-transparent text-left font-[inherit] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]",
                            milestoneStatusPillTone(m.status) === "ok" && "t-pill-ok",
                            milestoneStatusPillTone(m.status) === "accent" && "t-pill-accent",
                            milestoneStatusPillTone(m.status) === "meta" && "t-pill-meta",
                            STATUS_PILL_ROW_COMPACT_CLASS
                          )}
                          data-testid={`milestone-status-trigger-${m.id}`}
                          aria-label="Change milestone status"
                        >
                          {(MILESTONE_STATUS_MENU.find((o) => o.value === m.status) || MILESTONE_STATUS_MENU[0]).label}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        sideOffset={6}
                        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
                        data-testid={`milestone-status-menu-${m.id}`}
                      >
                        <div className="flex flex-col gap-1">
                          {MILESTONE_STATUS_MENU.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={cn(
                                "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]",
                                opt.value === m.status && "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]"
                              )}
                              onClick={() => {
                                updateStatus(m, opt.value);
                                setMilestoneStatusMenuOpenId(null);
                              }}
                              data-testid={`milestone-status-option-${opt.value}-${m.id}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <StatusPill status={m.status} className={STATUS_PILL_ROW_COMPACT_CLASS} />
                  )}
                </div>
                <div
                  className="min-w-0 flex-1 font-heading !font-light text-xl tracking-tight leading-snug"
                  data-testid={`milestone-title-display-${m.id}`}
                >
                  {m.title}
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {isAdmin ? (
                    <Popover open={milestoneResponsibleMenuOpenId === m.id} onOpenChange={(open) => setMilestoneResponsibleMenuOpenId(open ? m.id : null)}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center border-0 bg-transparent p-0 text-left hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]"
                          data-testid={`milestone-responsible-trigger-${m.id}`}
                          aria-label={responsibleAriaLabel(m.responsible)}
                        >
                          {renderResponsibleMark(m.responsible)}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        sideOffset={6}
                        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
                        data-testid={`milestone-responsible-menu-${m.id}`}
                      >
                        <div className="flex flex-col gap-1">
                          {responsibleMenuOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={cn(
                                "flex items-center justify-center rounded-[var(--t-event-pill-radius)] px-3 py-2 transition-colors hover:bg-black/[0.04]",
                                opt.value === String(m.responsible || "").toLowerCase() && "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]"
                              )}
                              onClick={() => {
                                updateResponsible(m, opt.value);
                                setMilestoneResponsibleMenuOpenId(null);
                              }}
                              data-testid={`milestone-responsible-option-${opt.value}-${m.id}`}
                              aria-label={responsibleAriaLabel(opt.value)}
                            >
                              {renderResponsibleMark(opt.value)}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span className="inline-flex shrink-0 items-center" aria-label={responsibleAriaLabel(m.responsible)}>
                      {renderResponsibleMark(m.responsible)}
                    </span>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => openMilestoneModal(m)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--t-event-pill-radius)] border border-transparent text-[var(--t-meta)] opacity-0 transition-[opacity,border-color,color,background-color] duration-150 group-hover:opacity-100 group-hover:border-[var(--t-border)] hover:text-[var(--t-text)] focus-visible:opacity-100 focus-visible:border-[var(--t-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)]"
                      aria-label={`Edit ${m.title}`}
                      data-testid={`milestone-edit-${m.id}`}
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
                {displayIntention ? (
                  <p className="t-meta mb-4" data-testid={`milestone-description-display-${m.id}`}>
                    {displayIntention}
                  </p>
                ) : null}
                {(() => {
                  const hasRange = Boolean(m.start_date || m.end_date);
                  const rangeLabel = hasRange ? formatMilestoneRangeDisplay(m.start_date, m.end_date) : "";
                  if (hasRange && rangeLabel) {
                    return (
                      <div className="t-meta text-xs mb-4" data-testid={`milestone-date-range-display-${m.id}`}>
                        {rangeLabel}
                      </div>
                    );
                  }
                  return null;
                })()}
                {(() => {
                  const t = (m.title || "").trim().toLowerCase();
                  const hasRange = Boolean(m.start_date || m.end_date);
                  const noDuePrefix = (t === "welcome session" || t === "schematic review" || t === "mid-build check-in" || t === "the reveal");
                  if (!hasRange && m.due_date) {
                    return (
                      <div className="t-meta text-xs mb-4" data-testid={`milestone-due-date-display-${m.id}`}>
                        {milestoneDueLabel(m, noDuePrefix)}
                      </div>
                    );
                  }
                  return null;
                })()}
                {m.deliverables.length > 0 && (
                  <div className="space-y-2 pl-1">
                    <div className="label-eyebrow mb-2">Checklist</div>
                    {m.deliverables.map((d) => (
                      <div key={d.id} className="flex items-center gap-3 min-w-0">
                        <button
                          type="button"
                          onClick={() => toggleDeliverable(m, d.id)}
                          disabled={!isAdmin}
                          className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[8px] border ${d.done ? "border-[var(--t-accent)] bg-[var(--t-accent)]" : "border-[var(--t-border)]"} ${!isAdmin ? "cursor-default" : ""}`}
                          aria-label={d.done ? `Mark ${d.label} incomplete` : `Mark ${d.label} complete`}
                          data-testid={`milestone-checklist-toggle-${d.id}`}
                        >
                          {d.done && <Check size={12} strokeWidth={2.5} className="text-white" />}
                        </button>
                        <span className={`text-sm min-w-0 ${d.done ? "text-[var(--t-meta)] line-through" : ""}`}>{d.label}</span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        );})}
        {milestones.filter((m) => m.phase === activePhase).length === 0 && !isAdmin && (
          <div className="flex items-center justify-center py-12 text-center" data-testid="milestones-empty">
            <p className="t-meta">No milestones in this phase.</p>
          </div>
        )}
        {isAdmin && (
          <div
            className={
              milestones.filter((m) => m.phase === activePhase).length === 0
                ? "flex items-center justify-center py-12 text-center"
                : "flex items-center justify-center pt-2 pb-2 text-center"
            }
            data-testid={milestones.filter((m) => m.phase === activePhase).length === 0 ? "milestones-empty" : undefined}
          >
            <button
              type="button"
              onClick={() => setCreateMilestoneOpen(true)}
              className="m-0 border-0 bg-transparent p-0 text-[15px] font-light text-[var(--t-accent)] cursor-pointer"
              data-testid="add-milestone-btn"
            >
              Add a milestone
            </button>
          </div>
        )}
      </div>

      {createMilestoneOpen && (
        <MilestoneEditModal
          milestone={null}
          phase={activePhase}
          clientId={client?.id}
          cycleId={isMonthly ? activeCycleId : null}
          clientContactName={portalContactDisplayName}
          onClose={() => setCreateMilestoneOpen(false)}
          onCreated={(created) => {
            if (created) {
              setMilestones((prev) => [...prev, created].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
            }
            setCreateMilestoneOpen(false);
          }}
        />
      )}

      {milestoneModalId && (
        <MilestoneEditModal
          milestone={milestones.find((item) => item.id === milestoneModalId) || null}
          clientContactName={portalContactDisplayName}
          onClose={() => setMilestoneModalId(null)}
          onSaved={(updated) => {
            if (updated) {
              setMilestones((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            }
            setMilestoneModalId(null);
          }}
          onDeleted={(deletedId) => {
            setMilestones((prev) => prev.filter((item) => item.id !== deletedId));
            setMilestoneModalId(null);
          }}
        />
      )}
    </>
  );
};

const parseDateValue = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const toDateValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseIsoToDateTime = (iso) => {
  if (!iso) return { date: "", time: "" };
  const localDate = parseLocalDateOnly(iso);
  if (localDate) {
    return { date: toDateValue(localDate), time: "" };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const date = toDateValue(d);
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
    return { date, time: "" };
  }
  return {
    date,
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

/** Range end: legacy end-of-day sentinel (23:59) stays date-only in the UI. */
const parseIsoToEndDateTime = (iso) => {
  if (!iso) return { date: "", time: "" };
  const localDate = parseLocalDateOnly(iso);
  if (localDate) {
    return { date: toDateValue(localDate), time: "" };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const date = toDateValue(d);
  if (d.getHours() === 23 && d.getMinutes() === 59 && d.getSeconds() === 0) {
    return { date, time: "" };
  }
  return {
    date,
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

/** Matches Messages thread topic dropdown surface (`PopoverContent` + `t-glass` on `MessagesPage`). */
const INLINE_PLANNING_DATE_PANEL_CLASS =
  "mt-3 max-w-full overflow-x-auto overflow-hidden neo-panel p-0 !shadow-none text-[15px] font-light font-[inherit] text-[var(--t-text)] outline-none transition-[border-color] duration-[160ms] ease-out t-glass [-webkit-overflow-scrolling:touch]";

/** Due date: calendar + time row, expanded inline under the trigger (no floating popover). */
const InlineDueDatePicker = ({ dueIso, onApply, display, testId }) => {
  const [expanded, setExpanded] = useState(false);
  const [calTick, setCalTick] = useState(0);
  const [datePart, setDatePart] = useState("");
  const [timePart, setTimePart] = useState("");
  const selected = useMemo(() => (datePart ? parseDateValue(datePart) : undefined), [datePart]);

  useEffect(() => {
    if (!expanded) return;
    const p = parseIsoToDateTime(dueIso);
    setDatePart(p.date);
    setTimePart(p.time);
  }, [expanded, dueIso]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  const resetDraft = () => {
    setDatePart("");
    setTimePart("");
    setCalTick((x) => x + 1);
  };

  return (
    <div className="mb-4">
      <button
        type="button"
        className={`t-meta text-xs hover:text-[var(--t-text)] ${expanded ? "text-[var(--t-text)]" : ""}`}
        data-testid={testId}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {display || (dueIso ? `Due ${formatMilestoneDueDisplay(dueIso)}` : "Set due date")}
      </button>
      {expanded && (
        <div className={INLINE_PLANNING_DATE_PANEL_CLASS} data-testid={`${testId}-panel`}>
          <Calendar
            key={`due-cal-${calTick}`}
            mode="single"
            selected={selected}
            onSelect={(date) => {
              setDatePart(toDateValue(date));
            }}
            initialFocus
            className="w-full min-w-0 max-w-full p-4"
          />
          <div className="flex flex-col gap-3 border-t border-[var(--t-border)] p-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <label className="label-eyebrow mb-1 block">Time</label>
              <TimePickerField
                value={timePart}
                onChange={setTimePart}
                data-testid={`${testId}-time`}
              />
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="t-btn-ghost min-h-[44px] px-3 py-2 text-xs"
                onClick={() => {
                  resetDraft();
                  if (dueIso) onApply(null);
                  setExpanded(false);
                }}
                data-testid={`${testId}-clear`}
              >
                Clear
              </button>
              <button
                type="button"
                className="neo-cta px-4 py-2 text-xs"
                disabled={!datePart}
                onClick={() => {
                  if (!datePart) return;
                  onApply({ date: datePart, time: timePart });
                  setExpanded(false);
                }}
                data-testid={`${testId}-apply`}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MilestoneEditModal = ({
  milestone,
  phase,
  clientId,
  cycleId,
  clientContactName,
  onClose,
  onSaved,
  onCreated,
  onDeleted,
}) => {
  const isCreate = !milestone;
  const contentRef = useRef(null);
  const timelineRef = useRef(null);
  const [timelineTouched, setTimelineTouched] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    status: "not_started",
    responsible: "taistu",
    startIso: "",
    endIso: "",
    deliverables: [],
    newChecklist: "",
    newChecklistDone: false,
  });
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const clientLabel = (clientContactName || "").trim() || "Client";

  useLayoutEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [milestone?.id, isCreate]);

  useEffect(() => {
    if (isCreate) {
      setForm({
        title: "",
        description: "",
        status: "not_started",
        responsible: "taistu",
        startIso: "",
        endIso: "",
        deliverables: [],
        newChecklist: "",
        newChecklistDone: false,
      });
      setTimelineTouched(false);
      setConfirmDelete(false);
      return;
    }
    const { startIso, endIso } = milestoneTimelineFromRecord(milestone);
    setForm({
      title: milestone.title || "",
      description: milestone.description || "",
      status: milestone.status || "not_started",
      responsible: milestone.responsible || "taistu",
      startIso,
      endIso,
      deliverables: (milestone.deliverables || []).map((item) => ({ ...item })),
      newChecklist: "",
      newChecklistDone: false,
    });
    setTimelineTouched(false);
    setConfirmDelete(false);
  }, [milestone, isCreate]);

  const updateDeliverable = (id, patch) => {
    setForm((prev) => ({
      ...prev,
      deliverables: prev.deliverables.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  };

  const removeDeliverable = (id) => {
    setForm((prev) => ({
      ...prev,
      deliverables: prev.deliverables.filter((item) => item.id !== id),
    }));
  };

  const addDeliverable = () => {
    const label = form.newChecklist.trim();
    if (!label) return;
    setForm((prev) => ({
      ...prev,
      newChecklist: "",
      newChecklistDone: false,
      deliverables: [...prev.deliverables, { id: crypto.randomUUID(), label, done: prev.newChecklistDone }],
    }));
  };

  const save = async () => {
    const title = form.title.trim();
    if (!title) {
      toast.error("Title is required");
      return false;
    }
    setBusy(true);
    try {
      let { startIso, endIso } = form;
      const pendingRange = timelineRef.current?.getPendingRange?.();
      if (pendingRange) {
        ({ startIso, endIso } = timelineRangeToIso(pendingRange));
        setTimelineTouched(true);
      }
      const payload = {
        title,
        description: form.description || "",
        status: form.status,
        responsible: form.responsible,
        deliverables: form.deliverables,
      };
      if (isCreate || timelineTouched || pendingRange) {
        payload.start_date = startIso || null;
        payload.end_date = endIso || null;
        payload.due_date = null;
      }
      if (isCreate) {
        if (!clientId) {
          toast.error("Client not found");
          return false;
        }
        const body = {
          phase: phase || "foundation",
          ...payload,
        };
        if (cycleId) body.cycle_id = cycleId;
        const { data } = await api.post(`/portal/milestones?client_id=${clientId}`, body);
        toast.success("Milestone added");
        onCreated?.(data);
        return true;
      }
      const { data } = await api.patch(`/portal/milestones/${milestone.id}`, payload);
      toast.success("Milestone saved");
      onSaved?.(data);
      return true;
    } catch {
      toast.error("Could not save milestone");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const deleteMilestone = async () => {
    if (isCreate) return;
    setBusy(true);
    try {
      await api.delete(`/portal/milestones/${milestone.id}`);
      toast.success("Milestone deleted");
      onDeleted(milestone.id);
    } catch {
      toast.error("Could not delete milestone");
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const requestClose = () => {
    if (confirmDelete) {
      setConfirmDelete(false);
      return;
    }
    if (busy) return;
    if (isCreate) {
      onClose();
      return;
    }
    void save();
  };

  return (
    <>
    <FullScreenModal
      onClose={requestClose}
      ariaLabel={isCreate ? "Add milestone" : "Edit milestone"}
      contentClassName="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-8"
      contentRef={contentRef}
      testid={isCreate ? "milestone-create-modal" : "milestone-edit-modal"}
    >
      <div className="label-eyebrow mb-3">Milestone</div>
      <h2 className="font-heading text-xl sm:text-2xl mb-6 tracking-tight">
        {isCreate ? "Add a milestone" : "Edit milestone"}
      </h2>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div>
          <label className="label-eyebrow block mb-2">Status</label>
          <div className="relative max-w-full sm:max-w-md">
            <select
              className="t-select !rounded-[var(--t-event-pill-radius)] bg-white/50 min-h-[44px] w-full appearance-none !px-4 !pr-10"
              value={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              data-testid="milestone-edit-status"
            >
              {MILESTONE_STATUS_MENU.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={16} strokeWidth={1.6} aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 opacity-70 text-[var(--t-meta)]" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Title</label>
          <input
            className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
            data-testid="milestone-edit-title"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Intention</label>
          <textarea
            rows={3}
            className="t-textarea !rounded-[var(--t-event-pill-radius)] bg-white/50"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            data-testid="milestone-edit-description"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Responsible</label>
            <div className="relative">
              <select
                className="t-select !rounded-[var(--t-event-pill-radius)] bg-white/50 min-h-[44px] w-full appearance-none !px-4 !pr-10"
                value={form.responsible}
                onChange={(e) => setForm((prev) => ({ ...prev, responsible: e.target.value }))}
                data-testid="milestone-edit-responsible"
              >
                <option value="taistu">Taī</option>
                <option value="shared">Shared</option>
                <option value="client">{clientLabel}</option>
              </select>
              <ChevronDown size={16} strokeWidth={1.6} aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 opacity-70 text-[var(--t-meta)]" />
            </div>
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Timeline</label>
          <InlineDateRangePicker
            ref={timelineRef}
            startIso={form.startIso}
            endIso={form.endIso}
            onChange={(range) => {
              setTimelineTouched(true);
              setForm((prev) => ({
                ...prev,
                ...timelineRangeToIso(range),
              }));
            }}
            testId="milestone-edit-range"
            label="Set timeline"
            className="mb-0"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Checklist</label>
          <div className="space-y-2">
            {form.deliverables.map((item) => (
              <div key={item.id} className="grid grid-cols-[1rem_minmax(0,1fr)_4.75rem] items-center gap-2">
                <button
                  type="button"
                  onClick={() => updateDeliverable(item.id, { done: !item.done })}
                  className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[8px] border ${item.done ? "border-[var(--t-accent)] bg-[var(--t-accent)]" : "border-[var(--t-border)]"}`}
                  data-testid={`milestone-edit-check-${item.id}`}
                >
                  {item.done && <Check size={12} strokeWidth={2.5} className="text-white" />}
                </button>
                <input
                  type="text"
                  className="t-input min-w-0 w-full !rounded-[var(--t-event-pill-radius)] bg-white/50"
                  value={item.label}
                  onChange={(e) => updateDeliverable(item.id, { label: e.target.value })}
                  data-testid={`milestone-edit-check-label-${item.id}`}
                />
                <button
                  type="button"
                  className="t-btn-ghost flex w-full shrink-0 items-center justify-start px-2 py-1 text-left text-xs"
                  onClick={() => removeDeliverable(item.id)}
                  data-testid={`milestone-edit-check-remove-${item.id}`}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="grid grid-cols-[1rem_minmax(0,1fr)_4.75rem] items-center gap-2">
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, newChecklistDone: !prev.newChecklistDone }))}
                className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[8px] border ${form.newChecklistDone ? "border-[var(--t-accent)] bg-[var(--t-accent)]" : "border-[var(--t-border)]"}`}
                data-testid="milestone-edit-check-new-toggle"
              >
                {form.newChecklistDone && <Check size={12} strokeWidth={2.5} className="text-white" />}
              </button>
              <input
                type="text"
                className="t-input min-w-0 w-full !rounded-[var(--t-event-pill-radius)] bg-white/50"
                placeholder="Add checklist item"
                value={form.newChecklist}
                onChange={(e) => setForm((prev) => ({ ...prev, newChecklist: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDeliverable();
                  }
                }}
                data-testid="milestone-edit-check-new"
              />
              <button
                type="button"
                className={`t-btn-ghost flex w-full shrink-0 items-center justify-start px-2 py-1 text-left text-xs ${form.newChecklist.trim() ? "text-[var(--t-accent)]" : ""}`}
                onClick={addDeliverable}
                data-testid="milestone-edit-check-add"
              >
                Add
              </button>
            </div>
          </div>
        </div>
        <div className={`flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center ${isCreate ? "sm:justify-end" : "sm:justify-between"}`}>
          {!isCreate ? (
          <button
            type="button"
            className="label-eyebrow inline-flex min-h-[44px] items-center border-0 bg-transparent !px-0 !py-0 text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)]"
            onClick={() => setConfirmDelete(true)}
            data-testid="milestone-edit-delete"
          >
            Delete milestone
          </button>
          ) : null}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onClose}
              className="label-eyebrow inline-flex min-h-[44px] items-center border-0 bg-transparent !px-0 !py-0 text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="label-eyebrow inline-flex min-h-[44px] items-center border-0 bg-transparent !px-0 !py-0 text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)] disabled:pointer-events-none disabled:opacity-40"
              disabled={busy}
              data-testid="milestone-edit-save"
            >
              {busy ? "Saving…" : isCreate ? "Add milestone" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </FullScreenModal>

    {!isCreate && (
    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent
        className="z-[80] max-w-md gap-5 border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-[var(--t-event-pill-radius)]"
        data-testid="milestone-edit-delete-confirm-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
            Delete milestone?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
            <span className="text-[var(--t-text)]">&ldquo;{milestone.title}&rdquo;</span> will be removed from the tracker. You cannot undo this.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-3 sm:justify-end">
          <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
            Cancel
          </AlertDialogCancel>
          <button
            type="button"
            className="neo-cta bg-[var(--t-danger,#b42318)] hover:opacity-90"
            onClick={deleteMilestone}
            disabled={busy}
            data-testid="milestone-edit-delete-confirm"
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    )}
    </>
  );
};
